import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Building2, FileDown, KeyRound, UserPlus, ShieldOff, RotateCw } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const empty = { name: "", adresse: "", telefon: "", email: "", notizen: "" };
const emptyPortal = { name: "", email: "", password: "" };

export default function Auftraggeber() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  // Portal-Zugang
  const [portalTarget, setPortalTarget] = useState(null); // auftraggeber object
  const [portalForm, setPortalForm] = useState(emptyPortal);
  const [portalRevoke, setPortalRevoke] = useState(null); // {auftraggeber, user}
  const [pwReset, setPwReset] = useState(null); // {auftraggeber, user}
  const [pwValue, setPwValue] = useState("");

  const load = async () => {
    const { data } = await api.get("/auftraggeber");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/auftraggeber/${editing.id}`, form);
        toast.success("Auftraggeber aktualisiert");
      } else {
        await api.post("/auftraggeber", form);
        toast.success("Auftraggeber erstellt");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/auftraggeber/${deleteId}`);
      toast.success("Gelöscht");
      setDeleteId(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const exportPdf = async (a) => {
    try {
      const res = await api.get(`/auftraggeber/${a.id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `Auftraggeber_${a.name.replace(/[^a-z0-9-_]/gi, "_")}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Export fehlgeschlagen");
    }
  };

  const openPortal = (a) => {
    setPortalTarget(a);
    // Für Zusatz-User email leer lassen — nur Firmenname als Default-Anzeigename
    const isFirst = !(a.linked_users?.length);
    setPortalForm({ ...emptyPortal, name: isFirst ? a.name : "", email: isFirst ? (a.email || "") : "" });
  };

  const createPortalUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", {
        email: portalForm.email.trim().toLowerCase(),
        password: portalForm.password,
        name: portalForm.name || portalTarget.name,
        role: "auftraggeber",
        auftraggeber_id: portalTarget.id,
      });
      toast.success("Benutzer hinzugefügt");
      setPortalTarget(null);
      setPortalForm(emptyPortal);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler beim Anlegen");
    }
  };

  const revokePortal = async () => {
    if (!portalRevoke) return;
    try {
      await api.delete(`/users/${portalRevoke.user.id}`);
      toast.success("Portal-Zugang entfernt");
      setPortalRevoke(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler beim Entfernen");
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (!pwReset) return;
    try {
      await api.patch(`/users/${pwReset.user.id}/password`, { password: pwValue });
      toast.success("Passwort geändert");
      setPwReset(null);
      setPwValue("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler beim Ändern");
    }
  };

  return (
    <div className="p-8 space-y-6 animate-fade-up" data-testid="auftraggeber-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Kundenverwaltung</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-2">Auftraggeber</h1>
          <p className="text-muted-foreground mt-1">Firmen &amp; Kunden mit optionalem Portal-Zugang.</p>
        </div>
        <Button onClick={openNew} data-testid="new-auftraggeber-button">
          <Plus className="h-4 w-4 mr-2" /> Neuer Auftraggeber
        </Button>
      </div>

      {items.length === 0 ? (
        <Card className="border-border">
          <CardContent className="p-12 text-center">
            <Building2 className="h-10 w-10 mx-auto text-muted-foreground" />
            <div className="font-heading text-xl font-semibold mt-4">Noch keine Auftraggeber</div>
            <div className="text-sm text-muted-foreground mt-1" data-testid="no-auftraggeber">
              Lege deinen ersten Auftraggeber an, um Anmeldungen zuzuordnen.
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="auftraggeber-list">
          {items.map((a) => (
            <Card key={a.id} className="border-border" data-testid={`auftraggeber-${a.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="h-9 w-9 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{a.name}</div>
                      {a.adresse && <div className="text-xs text-muted-foreground mt-0.5 truncate">{a.adresse}</div>}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => exportPdf(a)} data-testid={`export-auftraggeber-${a.id}`} title="PDF-Export">
                      <FileDown className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(a)} data-testid={`edit-auftraggeber-${a.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteId(a.id)} data-testid={`delete-auftraggeber-${a.id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-4 text-xs">
                  {a.telefon && <div><span className="text-muted-foreground">Tel: </span><span>{a.telefon}</span></div>}
                  {a.email && <div className="col-span-2 truncate"><span className="text-muted-foreground">E-Mail: </span><span>{a.email}</span></div>}
                </div>

                {/* Portal-Zugänge (mehrere Benutzer möglich) */}
                <div className="mt-4 pt-3 border-t border-border" data-testid={`portal-section-${a.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <KeyRound className="h-3 w-3" /> Portal-Zugänge
                    </div>
                    {(a.linked_users?.length || 0) > 0 ? (
                      <Badge variant="outline" className="text-[10px] bg-emerald-100/50 border-emerald-300 text-emerald-900 dark:bg-emerald-900/20 dark:text-emerald-300">
                        {a.linked_users.length} aktiv
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] text-muted-foreground">Nicht angelegt</Badge>
                    )}
                  </div>
                  {(a.linked_users?.length || 0) > 0 && (
                    <ul className="mt-2 divide-y divide-border/70 rounded-md border border-border/70" data-testid={`portal-users-${a.id}`}>
                      {a.linked_users.map((u) => (
                        <li key={u.id} className="flex items-center justify-between gap-2 px-2 py-1.5" data-testid={`portal-user-${u.id}`}>
                          <div className="min-w-0">
                            <div className="text-xs truncate" title={u.email}>{u.email}</div>
                            {u.name && <div className="text-[10px] text-muted-foreground truncate">{u.name}</div>}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setPwReset({ auftraggeber: a, user: u }); setPwValue(""); }}
                              data-testid={`portal-pw-reset-${u.id}`}
                              className="h-7 text-[10px] px-2"
                              title="Passwort ändern"
                            >
                              <RotateCw className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setPortalRevoke({ auftraggeber: a, user: u })}
                              data-testid={`portal-revoke-${u.id}`}
                              className="h-7 text-[10px] px-2 text-destructive hover:text-destructive"
                              title="Zugang entfernen"
                            >
                              <ShieldOff className="h-3 w-3" />
                            </Button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openPortal(a)}
                      data-testid={`portal-create-${a.id}`}
                      className="h-7 text-[11px] w-full"
                    >
                      <UserPlus className="h-3.5 w-3.5 mr-1" />
                      {(a.linked_users?.length || 0) > 0 ? "Weiteren Benutzer hinzufügen" : "Portal-Zugang erstellen"}
                    </Button>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground">
                  <span className="font-bold text-foreground">{a.anmeldungen_count ?? 0}</span> Anmeldungen zugeordnet
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Auftraggeber-Formular */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="auftraggeber-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">
              {editing ? "Auftraggeber bearbeiten" : "Neuer Auftraggeber"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name">Name / Firma *</Label>
              <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="ag-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="adresse">Adresse</Label>
              <Input id="adresse" value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} data-testid="ag-adresse" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="telefon">Telefon</Label>
                <Input id="telefon" value={form.telefon} onChange={(e) => setForm({ ...form, telefon: e.target.value })} data-testid="ag-telefon" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email">E-Mail</Label>
                <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ag-email" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="notizen">Notizen</Label>
              <Textarea id="notizen" rows={2} value={form.notizen} onChange={(e) => setForm({ ...form, notizen: e.target.value })} data-testid="ag-notizen" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="ag-cancel">Abbrechen</Button>
              <Button type="submit" data-testid="ag-submit">{editing ? "Aktualisieren" : "Erstellen"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Portal-Zugang erstellen */}
      <Dialog open={!!portalTarget} onOpenChange={(v) => { if (!v) { setPortalTarget(null); setPortalForm(emptyPortal); } }}>
        <DialogContent data-testid="portal-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" />
              {portalTarget?.linked_users?.length ? "Weiteren Benutzer hinzufügen" : "Portal-Zugang erstellen"}
            </DialogTitle>
            <DialogDescription>
              Für <span className="font-medium text-foreground">{portalTarget?.name}</span>.
              Jeder hinzugefügte Benutzer sieht nur Anmeldungen &amp; Projekte dieses Auftraggebers —
              du kannst also z.B. mehrere Mitarbeiter des Kunden anlegen.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createPortalUser} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="portal-name">Ansprechpartner</Label>
              <Input id="portal-name" value={portalForm.name} onChange={(e) => setPortalForm({ ...portalForm, name: e.target.value })} required data-testid="portal-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="portal-email">Login-E-Mail *</Label>
              <Input id="portal-email" type="email" value={portalForm.email} onChange={(e) => setPortalForm({ ...portalForm, email: e.target.value })} required data-testid="portal-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="portal-password">Passwort * (mind. 6 Zeichen)</Label>
              <Input id="portal-password" type="text" minLength={6} value={portalForm.password} onChange={(e) => setPortalForm({ ...portalForm, password: e.target.value })} required data-testid="portal-password" placeholder="Wird dem Kunden mitgeteilt" />
              <p className="text-[11px] text-muted-foreground">Das Passwort wird nur diesem Kunden mitgeteilt. Änderungen aktuell nur durch Neuanlage möglich.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setPortalTarget(null); setPortalForm(emptyPortal); }}>Abbrechen</Button>
              <Button type="submit" data-testid="portal-submit">Zugang anlegen</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Auftraggeber löschen */}
      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Auftraggeber löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Die Anmeldungen bleiben erhalten, werden aber nicht mehr diesem Auftraggeber zugeordnet.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="ag-delete-confirm">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Portal-Zugang widerrufen */}
      <AlertDialog open={!!portalRevoke} onOpenChange={(v) => !v && setPortalRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Portal-Zugang entfernen?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-foreground">{portalRevoke?.user.email}</span> kann sich danach nicht mehr einloggen. Der Auftraggeber selbst bleibt erhalten.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={revokePortal} data-testid="portal-revoke-confirm">Zugang entfernen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Passwort zurücksetzen */}
      <Dialog open={!!pwReset} onOpenChange={(v) => { if (!v) { setPwReset(null); setPwValue(""); } }}>
        <DialogContent data-testid="pw-reset-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
              <RotateCw className="h-5 w-5 text-primary" /> Passwort ändern
            </DialogTitle>
            <DialogDescription>
              Neues Passwort für <span className="font-medium text-foreground">{pwReset?.user.email}</span>. Der Benutzer muss sich mit dem neuen Passwort erneut anmelden.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={resetPassword} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="pw-new">Neues Passwort * (mind. 6 Zeichen)</Label>
              <Input id="pw-new" type="text" minLength={6} value={pwValue} onChange={(e) => setPwValue(e.target.value)} required data-testid="pw-new" placeholder="Wird dem Kunden mitgeteilt" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setPwReset(null); setPwValue(""); }}>Abbrechen</Button>
              <Button type="submit" data-testid="pw-submit">Passwort ändern</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
